import { FlowData } from '@/shared/components/FlowContainer/types'
import type { SprintPurposeOption } from '@/shared/components/Purpose'
import type { SprintActivity, SprintActivityGameOptions, SprintEmailContent } from 'pure-js/types/Sprint'
import { ActivityType } from '../../../../../pure-js/enums/ActivityType'

export { ActivityType } from '../../../../../pure-js/enums/ActivityType'

export enum TrainingPurpose {
  AWARE = 'aware',
  TRAIN = 'train',
  CUSTOM = 'custom'
}

export enum SessionType {
  WORKSHOP = 'workshop',
  PRESENTATION = 'presentation',
  DISCUSSION = 'discussion',
  EXERCISE = 'exercise',
  INTRODUCTION = 'introduction',
  SESSION = 'session',
  SHORT_DEBRIEF = 'shortDebrief',
  LONG_DEBRIEF = 'longDebrief',
  FIRST_GAME_SESSION = 'first_game_session',
  SHORT_GAME_SESSION = 'short_game_session',
  LONG_GAME_SESSION = 'long_game_session',
  GROUP_REFLECTION = 'group_reflection'
}

export enum ValidationErrorType {
  PURPOSE_REQUIRED = 'purposeRequired',
  ACTIVITIES_REQUIRED = 'activitiesRequired',
  PARTICIPANTS_REQUIRED = 'participantsRequired',
  TEAMS_REQUIRED = 'teamsRequired',
  UNASSIGNED_PARTICIPANTS = 'unassignedParticipants',
  INSUFFICIENT_TEAM_MEMBERS = 'insufficientTeamMembers',
  ALL_ACTIVITIES_MUST_BE_SCHEDULED = 'allActivitiesMustBeScheduled',
  START_DATE_REQUIRED = 'startDateRequired',
  END_DATE_REQUIRED = 'endDateRequired'
}

export enum DragDropType {
  PARTICIPANT = 'participant',
  TEAM = 'team',
  UNASSIGNED = 'unassigned'
}

export interface Activity extends SprintActivityGameOptions, Pick<SprintActivity, 'dataCustomizationVariantId'> {
  id: string
  type: ActivityType
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
  duration: number // in minutes
  /** Display-only: minutes added to slot for planning UI (slot + buffer); not sent to game/backend. */
  slotDisplayBufferMinutes?: number | null
  limit?: number | null // Maximum number of this activity type (null = unlimited)
  isFixed?: boolean // Whether this activity is fixed and cannot be removed or reordered
  scheduledDate?: string
  scheduledTime?: string
  endTime?: string
}

export interface Participant {
  id: string
  name: string
  email: string
  role?: string
}

export interface Team {
  id: string
  name: string
  participants: Participant[]
  maxParticipants: number
  language?: string // Language code (e.g., 'en', 'zh', 'pt-BR')
}

export interface TrainingSprintConfig extends FlowData {
  purpose: TrainingPurpose
  sprintName?: string // Only required for custom purpose
  activities: Activity[]
  participants: Participant[]
  teams: Team[]
  startDate: string
  endDate: string
  emailContent?: SprintEmailContent
}

export interface TrainingSprintStepProps {
  data: TrainingSprintConfig
  updateData: <K extends keyof TrainingSprintConfig>(key: K, value: TrainingSprintConfig[K]) => void
  stepId: string
  isActive: boolean
  isCompleted: boolean
  isVisited: boolean
  hasValidationErrors?: boolean
  validationErrors?: string[]
}

export type PurposeOption = SprintPurposeOption
